export type Platform = 'android' | 'ios' | 'web';
export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Browser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'other';

export interface PlatformBridge {
  platform: Platform;
  isNative: boolean;
  deviceType: DeviceType;
  browser: Browser;
  isIOS: boolean;
  isMacOS: boolean;
  isSafari: boolean;
  isStandalone: boolean;
  width: number;
}

export interface MFEBridge {
  getPlatform(): Platform;
  navigate?(path: string): void;
}

declare global {
  interface Window {
    MFEBridge?: MFEBridge;
  }
}

/**
 * Detects the browser type based on user agent string
 * @returns The detected browser type
 */
const detectBrowser = (): Browser => {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('edg')) return 'edge';
  return 'other';
};

/**
 * Determines the device type based on screen width
 * @param width - The screen width in pixels
 * @returns The detected device type
 */
const detectDeviceType = (width: number): DeviceType => {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Detects the platform type based on environment and user agent
 * @returns The detected platform type
 */
const detectPlatform = (): Platform => {
  // Check if we're in a native environment
  if (window.MFEBridge) {
    return window.MFEBridge.getPlatform();
  }

  // Check user agent for platform hints
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';

  return 'web';
};

/**
 * Gets comprehensive platform information
 * @returns Platform information including device type, browser, and dimensions
 */
export const getPlatformInfo = (): PlatformBridge => {
  const width = window.innerWidth;
  const platform = detectPlatform();
  const browser = detectBrowser();
  const platformStr = navigator.platform.toLowerCase();

  return {
    platform,
    isNative: platform !== 'web',
    deviceType: detectDeviceType(width),
    browser,
    isIOS: platform === 'ios',
    isMacOS: platformStr.includes('mac'),
    isSafari: browser === 'safari',
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    width,
  };
};

/**
 * Initializes platform information listener
 * @param callback - Function to be called when platform information changes
 * @returns Cleanup function to remove event listeners
 */
export const initPlatformListener = (callback: (info: PlatformBridge) => void): (() => void) => {
  const updateInfo = (): void => {
    callback(getPlatformInfo());
  };

  // Initial call
  updateInfo();

  // Listen for resize events
  window.addEventListener('resize', updateInfo);

  // Listen for orientation changes
  window.addEventListener('orientationchange', updateInfo);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', updateInfo);
    window.removeEventListener('orientationchange', updateInfo);
  };
};
